import { useCallback, useEffect, useRef, useState } from 'react';

type PanTo = (target: { x?: number; y?: number }) => void;

type ScrollbarMetrics = {
  visible: boolean;
  thumbSize: number;
  thumbPosition: number;
  maxScroll: number;
};

export const useScrollbarDrag = (
  panTo: PanTo | undefined,
  horizontalMetrics: ScrollbarMetrics,
  verticalMetrics: ScrollbarMetrics,
  horizontalTrackRef: React.RefObject<HTMLDivElement>,
  verticalTrackRef: React.RefObject<HTMLDivElement>
) => {
  const [isDraggingH, setIsDraggingH] = useState(false);
  const [isDraggingV, setIsDraggingV] = useState(false);

  const dragStartRef = useRef<{
    x: number;
    y: number;
    translateX: number;
    translateY: number;
  } | null>(null);

  // Capture latest metrics/panTo in refs so the mousemove handler stays stable
  const hMetricsRef = useRef(horizontalMetrics);
  hMetricsRef.current = horizontalMetrics;
  const vMetricsRef = useRef(verticalMetrics);
  vMetricsRef.current = verticalMetrics;
  const panToRef = useRef(panTo);
  panToRef.current = panTo;

  const resolveCurrentTranslate = useCallback(() => {
    const mH = hMetricsRef.current;
    const trackWidth =
      horizontalTrackRef.current?.getBoundingClientRect().width ?? 0;
    const ratioH =
      trackWidth - mH.thumbSize > 0
        ? mH.thumbPosition / (trackWidth - mH.thumbSize)
        : 0;

    const mV = vMetricsRef.current;
    const trackHeight =
      verticalTrackRef.current?.getBoundingClientRect().height ?? 0;
    const ratioV =
      trackHeight - mV.thumbSize > 0
        ? mV.thumbPosition / (trackHeight - mV.thumbSize)
        : 0;

    return {
      translateX: -ratioH * mH.maxScroll,
      translateY: -ratioV * mV.maxScroll,
    };
  }, [horizontalTrackRef, verticalTrackRef]);

  const handleHorizontalMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDraggingH(true);
      const { translateX, translateY } = resolveCurrentTranslate();
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        translateX,
        translateY,
      };
    },
    [resolveCurrentTranslate]
  );

  const handleVerticalMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDraggingV(true);
      const { translateX, translateY } = resolveCurrentTranslate();
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        translateX,
        translateY,
      };
    },
    [resolveCurrentTranslate]
  );

  const handleHorizontalTrackClick = useCallback(
    (e: React.MouseEvent) => {
      const track = horizontalTrackRef.current;
      const m = hMetricsRef.current;
      if (!track || !m.visible) return;

      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const trackWidth = rect.width;

      const targetThumbPos = clickX - m.thumbSize / 2;
      const clampedPos = Math.max(
        0,
        Math.min(targetThumbPos, trackWidth - m.thumbSize)
      );
      const ratio = clampedPos / (trackWidth - m.thumbSize);

      panToRef.current?.({ x: -ratio * m.maxScroll });
    },
    [horizontalTrackRef]
  );

  const handleVerticalTrackClick = useCallback(
    (e: React.MouseEvent) => {
      const track = verticalTrackRef.current;
      const m = vMetricsRef.current;
      if (!track || !m.visible) return;

      const rect = track.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const trackHeight = rect.height;

      const targetThumbPos = clickY - m.thumbSize / 2;
      const clampedPos = Math.max(
        0,
        Math.min(targetThumbPos, trackHeight - m.thumbSize)
      );
      const ratio = clampedPos / (trackHeight - m.thumbSize);

      panToRef.current?.({ y: -ratio * m.maxScroll });
    },
    [verticalTrackRef]
  );

  // Global mouse move/up while dragging
  useEffect(() => {
    if (!isDraggingH && !isDraggingV) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;

      if (isDraggingH && horizontalTrackRef.current) {
        const trackWidth =
          horizontalTrackRef.current.getBoundingClientRect().width;
        const m = hMetricsRef.current;
        const ratio = (e.clientX - start.x) / (trackWidth - m.thumbSize);
        panToRef.current?.({ x: start.translateX - ratio * m.maxScroll });
      }

      if (isDraggingV && verticalTrackRef.current) {
        const trackHeight =
          verticalTrackRef.current.getBoundingClientRect().height;
        const m = vMetricsRef.current;
        const ratio = (e.clientY - start.y) / (trackHeight - m.thumbSize);
        panToRef.current?.({ y: start.translateY - ratio * m.maxScroll });
      }
    };

    const handleMouseUp = () => {
      setIsDraggingH(false);
      setIsDraggingV(false);
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingH, isDraggingV, horizontalTrackRef, verticalTrackRef]);

  return {
    horizontal: {
      isDragging: isDraggingH,
      onThumbMouseDown: handleHorizontalMouseDown,
      onTrackClick: handleHorizontalTrackClick,
    },
    vertical: {
      isDragging: isDraggingV,
      onThumbMouseDown: handleVerticalMouseDown,
      onTrackClick: handleVerticalTrackClick,
    },
  };
};
